import { useMemo, useLayoutEffect, useRef } from "react";

import PropTypes from "prop-types";

import DirectoryTabContent from "./DirectoryTabContent";
import DirectoryTabItem from "./DirectoryTabItem";
import { containerCss, tabsContainerCss } from "./DirectoryTabs.style";

const propTypes = {
  /**
   * The tab items.
   * Tab items properties: id, title, content
   */
  defaultActiveTab: PropTypes.number,
  items: PropTypes.arrayOf(
    PropTypes.exact({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      content: PropTypes.node.isRequired,
      hide: PropTypes.bool,
    }),
  ).isRequired,
  onChange: PropTypes.func,
  /**
   * Always render the tabs in the DOM
   */
  alwaysRender: PropTypes.bool,
};

const defaultProps = {
  defaultActiveTab: 0,
};

const DirectoryTabs = ({
  defaultActiveTab,
  items: initialItems,
  onChange,
  alwaysRender,
}) => {
  const items = useMemo(
    () => initialItems.filter((item) => !item.hide),
    [initialItems],
  );
  const tabsButton = useRef({});
  const isFirstFocus = useRef(true);

  useLayoutEffect(() => {
    if (isFirstFocus.current) {
      isFirstFocus.current = false;
    } else {
      tabsButton.current[defaultActiveTab].focus();
    }
  }, [defaultActiveTab]);

  const renderTabs = () => (
    <ol css={tabsContainerCss} role="tablist">
      {items.map(({ id, title }) => (
        <DirectoryTabItem
          key={id}
          id={id}
          title={title}
          activeTab={defaultActiveTab}
          setActiveTab={onChange}
          ref={(e) => {
            tabsButton.current[id] = e;
          }}
        />
      ))}
    </ol>
  );

  if (items.length === 1) return items[0].content;

  return (
    <div css={containerCss}>
      {items.length > 1 && renderTabs()}

      {items
        .filter((item) => alwaysRender || item.id === defaultActiveTab)
        .map((item) => (
          <DirectoryTabContent
            key={item.id}
            id={item.id}
            activeTab={defaultActiveTab}
            content={item.content}
          />
        ))}
    </div>
  );
};

DirectoryTabs.propTypes = propTypes;
DirectoryTabs.defaultProps = defaultProps;

export default DirectoryTabs;
